'use client';

import React, { memo, useCallback, useState } from 'react';

import useProductSelect from '@/hooks/useProductSelect';
import { appendImageSize } from '@/utils/products';

import {
  AutoSelection,
  Box,
  EmptySearchResult,
  Icon,
  InlineStack,
  Link,
  Listbox,
  Popover,
  Scrollable,
  SkeletonThumbnail,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@shopify/polaris-icons';
import { Product } from '@/types/product';

export type ProductSelectProps = {
  selectedProduct: Product | null;
  onSelect: (product: Product) => void;
};

const Activator = ({
  selectedProduct,
  open,
  handleToggleOpen,
}: {
  selectedProduct: Product | null;
  open: boolean;
  handleToggleOpen: () => void;
}) => (
  <Link monochrome removeUnderline onClick={handleToggleOpen}>
    <Box
      width="400px"
      background="bg-surface"
      padding="200"
      borderRadius="200"
      borderWidth="025"
      borderColor="border"
      borderStyle="solid"
    >
      <InlineStack gap="200" blockAlign="center">
        {selectedProduct ? (
          <>
            {selectedProduct?.imageUrl ? (
              <Thumbnail
                source={appendImageSize(selectedProduct?.imageUrl || '')}
                alt={selectedProduct?.title}
                size="large"
              />
            ) : (
              <SkeletonThumbnail size="large" />
            )}
            <Text as="span">
              <span className="line-clamp-2">{selectedProduct?.title}</span>
            </Text>
          </>
        ) : (
          <div className="flex h-[60px] items-center">
            <Text as="span">Select a product</Text>
          </div>
        )}
        <Icon source={open ? ChevronUpIcon : ChevronDownIcon} />
      </InlineStack>
    </Box>
  </Link>
);

const ProductSelect: React.FC<ProductSelectProps> = ({ onSelect, selectedProduct }) => {
  const [activeOptionId, setActiveOptionId] = useState<string>('');
  const { open, setOpen, query, setQuery, options, loadProducts, isLoading } = useProductSelect();

  const handleToggleOpen = useCallback(() => {
    const isOpen = !open;
    if (isOpen) {
      loadProducts();
    } else {
      setQuery('');
    }
    setOpen(isOpen);
  }, [loadProducts, open, setOpen, setQuery]);

  const handleProductSelect = (productId: string): void => {
    const product = options.find((option) => option.productId === productId);

    setOpen(false);
    if (product) {
      onSelect(product);
    }
  };

  const handleActiveOptionChange = (_: string, domId: string) => {
    setActiveOptionId(domId);
  };

  const listboxId = 'ProductsSelect';

  const renderSearchField = () => (
    <Box padding="200">
      <TextField
        focused={true}
        clearButton
        label="Search products"
        labelHidden
        prefix={<Icon source={SearchIcon} />}
        placeholder="Search products"
        value={query}
        autoComplete="off"
        onChange={setQuery}
        onClearButtonClick={() => setQuery('')}
        ariaActiveDescendant={activeOptionId}
        ariaControls={listboxId}
      />
    </Box>
  );

  const renderOptions = () =>
    options.map(({ productId, title, imageUrl }) => {
      const selected = productId === selectedProduct?.productId;
      return (
        <Listbox.Option key={productId} value={productId!} selected={selected}>
          <Listbox.TextOption selected={selected}>
            <InlineStack gap="200" blockAlign="center">
              {imageUrl ? (
                <Thumbnail source={appendImageSize(imageUrl || '')} alt={title} size="small" />
              ) : (
                <SkeletonThumbnail size="small" />
              )}
              <Text as="span">
                <span className="line-clamp-2">{title}</span>
              </Text>
            </InlineStack>
          </Listbox.TextOption>
        </Listbox.Option>
      );
    });

  const renderNoResults = () => (
    <EmptySearchResult title="" description={query ? `No products found matching "${query}"` : 'No products found'} />
  );

  const renderListLoading = () =>
    isLoading ? <Listbox.Loading accessibilityLabel={`${query ? 'Filtering' : 'Loading'} products`} /> : null;

  const renderList = () => (
    <Listbox
      enableKeyboardControl
      autoSelection={AutoSelection.FirstSelected}
      accessibilityLabel="Search for and select a product"
      customListId={listboxId}
      onSelect={handleProductSelect}
      onActiveOptionChange={handleActiveOptionChange}
    >
      {options.length ? renderOptions() : renderNoResults()}
      {renderListLoading()}
    </Listbox>
  );

  return (
    <Popover
      active={open}
      activator={<Activator selectedProduct={selectedProduct} open={open} handleToggleOpen={handleToggleOpen} />}
      preferredAlignment="left"
      autofocusTarget="first-node"
      onClose={() => setOpen(false)}
      ariaHaspopup="listbox"
    >
      <Popover.Pane fixed>
        {renderSearchField()}
        <Scrollable className="h-[250px]" onScrolledToBottom={loadProducts}>
          {renderList()}
        </Scrollable>
      </Popover.Pane>
    </Popover>
  );
};

export default memo(ProductSelect);
